import { Form, Formik } from 'formik';
import React from 'react';

import { PrimaryButton } from '@components/Button/variants';
import FirstPartyDataFields from '@components/OnboardingModal/ProfileFields/FirstPartyDataFields';
import { useEventTracking } from '@hooks';
import { useProfileUpdate } from '@hooks';
import { ClickTarget, click } from '@tracking';
import { User } from '@utils/backend-api/currentUser';
import { PROFILE_FIELD_OTHER_VALUE } from '@utils/backend-api/profileSettings';

import { InitialStateProps } from '..';
import SettingHeaderSeperator from '../../SettingHeaderSeperator';
import schema from '../schema';
import styles from '../styles.module.css';
import BasicInfoForm from './BasicInfoForm';

interface UserFormProps {
  currentUser: User;
  initialState: InitialStateProps;
  ReadOnlyMode: string;
}

const UserForm = ({ ReadOnlyMode, initialState, currentUser }: UserFormProps) => {
  const { saveProfile } = useProfileUpdate({ userId: currentUser?.id, isSetting: true });
  const { trackEvent } = useEventTracking();

  const handleSubmit = (values: InitialStateProps, { setSubmitting, resetForm }) => {
    const sanitizedParams = {
      email: values.email,
      password: values.password,
      name: values.name,
      company_name: values.company?.label || '',
      company_size: values.companySize.value,
      job_level:
        values.jobLevel.value === PROFILE_FIELD_OTHER_VALUE
          ? values.customJobLevel.label
          : values.jobLevel.value,
      job_function:
        values.jobFunction.value === PROFILE_FIELD_OTHER_VALUE
          ? values.customJobFunction.label
          : values.jobFunction.value,
      sub_job_function:
        values.subJobFunction.value === PROFILE_FIELD_OTHER_VALUE
          ? values.customSubJobFunction.label
          : values.subJobFunction.value,
      organization_industry:
        values.industry.value === PROFILE_FIELD_OTHER_VALUE
          ? values.customIndustry.label
          : values.industry.value,
      organization_sub_industry:
        values.subIndustry.value === PROFILE_FIELD_OTHER_VALUE
          ? values.customSubIndustry.label
          : values.subIndustry.value,

      postal_code: values.postalCode,
    };
    saveProfile(sanitizedParams);
    trackEvent(click({ clickTarget: ClickTarget.Update, clickText: 'Update' }));

    resetForm({
      values: {
        ...values,
        password: '',
        passwordConfirmation: '',
      },
    });
    setSubmitting(false);
  };

  return (
    <Formik
      enableReinitialize
      onSubmit={(values, actions) => handleSubmit(values, actions)}
      initialValues={initialState}
      validationSchema={schema}
    >
      {(formik) => (
        <Form data-testid="user-form-container">
          <BasicInfoForm loginLinkOnly={currentUser?.login_link_only} />
          {currentUser?.is_subscriber && (
            <>
              <SettingHeaderSeperator />
              <div className={styles.userFormSettings}>
                <FirstPartyDataFields isSetting />
              </div>
            </>
          )}
          {!ReadOnlyMode && (
            <div className={styles.submitBtnContainer}>
              <PrimaryButton
                as="button"
                type="submit"
                disabled={!formik.isValid || !formik.dirty || formik.isSubmitting}
                isLoading={formik.isSubmitting}
                fullWidth
              >
                Update
              </PrimaryButton>
            </div>
          )}
        </Form>
      )}
    </Formik>
  );
};

export default UserForm;
